(() => {
  "use strict";
  const ENABLED = true;
  const GA4_ID = "G-1S40YDLX5B";
  const DETAIL_EVENTS = new Set(["configurator_step_viewed", "configurator_option_selected", "configurator_next", "configurator_back", "configurator_closed", "configurator_validation_failed", "configurator_price_reviewed", "configurator_cart_requested", "configurator_cart_succeeded", "configurator_cart_failed", "configurator_restarted"]);
  const STEPS = new Set(["type", "method", "details", "function", "solution", "review"]);
  const OPTIONS = Object.freeze({ type: ["single_vision", "prescription_reading", "readers"], method: ["upload", "manual", "later"], function: ["clear", "blue_light_filtering", "photochromic", "tinted_prescription"], solution: ["index_161", "index_167", "index_174"] });
  function safeDetail(event, input) {
    if (!DETAIL_EVENTS.has(event) || !input || !STEPS.has(input.step) || !Number.isInteger(input.visit_index) || input.visit_index < 1 || input.visit_index > 1000) return null;
    const out = { step: input.step, visit_index: input.visit_index };
    if (["upload", "manual", "later", "none"].includes(input.rx_method)) out.rx_method = input.rx_method;
    if (Number.isInteger(input.elapsed_ms) && input.elapsed_ms >= 0 && input.elapsed_ms <= 86400000) out.elapsed_ms = input.elapsed_ms;
    if (event === "configurator_option_selected") {
      if (!Object.hasOwn(OPTIONS, input.option_group) || !OPTIONS[input.option_group].includes(input.option_value)) return null;
      out.option_group = input.option_group;
      out.option_value = input.option_value;
    }
    if (event === "configurator_validation_failed" || event === "configurator_cart_failed") {
      const codes = event === "configurator_cart_failed" ? ["cart_unconfirmed"] : ["choice_required", "input_invalid", "upload_incomplete"];
      if (!codes.includes(input.failure_code)) return null;
      out.failure_code = input.failure_code;
    }
    if (event === "configurator_price_reviewed" && input.currency === "USD" && typeof input.value === "number" && Number.isFinite(input.value) && input.value >= 0 && input.value <= 10000) {
      out.currency = "USD";
      out.value = Math.round(input.value * 100) / 100;
    }
    return out;
  }
  const PUBLIC_EVENTS = new Set(["tryon_opened", "tryon_first_render_ready", "configurator_opened", "configuration_completed", ...DETAIL_EVENTS]);
  if (window.ElookusBehaviorV1?.mode === "ga4") return;

  function createGA4Transport(env) {
    const timers = new Set(), pending = new Set();
    const stats = { submitted_to_gtag: 0, dropped: 0 };
    let generation = 0, accepted = 0;
    function reset() {
      generation += 1;
      for (const timer of timers) { try { env.cancel(timer); } catch {} }
      timers.clear();
      stats.dropped += pending.size;
      pending.clear();
    }
    function permitted() {
      try { return env.consent() === "granted"; } catch { return false; }
    }
    function send(record, current = () => true) {
      if (!permitted() || !PUBLIC_EVENTS.has(record?.event_name) || !["product", "collection"].includes(record?.entry_point) || pending.size >= 32 || accepted >= 500) return false;
      let origin;
      try { origin = env.origin(); } catch { return false; }
      if (!["https://elookus.com", "https://www.elookus.com", "https://w7u3mc-bq.myshopify.com"].includes(origin)) return false;
      const internalEvent = record.event_name;
      const detail = DETAIL_EVENTS.has(internalEvent) ? safeDetail(internalEvent, record) : {};
      if (!detail) return false;
      const viewEvents = { type: "configurator_view_type", method: "configurator_view_method", details: "configurator_view_details", function: "configurator_view_function", solution: "configurator_view_solution", review: "configurator_view_review" };
      const methodEvents = { upload: "configurator_method_upload", manual: "configurator_method_manual", later: "configurator_method_later" };
      const eventName = internalEvent === "configurator_step_viewed" ? viewEvents[detail.step]
        : internalEvent === "configurator_option_selected" && detail.option_group === "method" ? methodEvents[detail.option_value]
          : internalEvent;
      const params = Object.freeze({
        send_to: GA4_ID,
        entry_point: record.entry_point,
        schema_version: DETAIL_EVENTS.has(internalEvent) ? 2 : 1,
        ...detail,
        page_location: origin + (record.entry_point === "collection" ? "/collections" : "/products"),
        page_referrer: "",
        page_title: "ELOOKUS UI"
      });
      const ticket = generation, deadline = env.now() + 2000, item = {};
      let attempts = 0;
      accepted += 1;
      pending.add(item);
      function drop() { if (pending.delete(item)) stats.dropped += 1; }
      function later(delay) {
        let timer;
        try {
          timer = env.schedule(() => { timers.delete(timer); attempt(); }, delay);
          timers.add(timer);
        } catch { drop(); }
      }
      function attempt() {
        if (ticket !== generation || !pending.has(item)) return;
        try {
          if (!permitted() || !current() || env.now() >= deadline || attempts >= 21) { drop(); return; }
          attempts += 1;
          const tag = env.tag();
          if (typeof tag === "function") {
            if (!permitted() || !current()) { drop(); return; }
            pending.delete(item);
            try { tag("event", eventName, params); stats.submitted_to_gtag += 1; } catch { stats.dropped += 1; }
            return;
          }
          later(100);
        } catch { drop(); }
      }
      later(0);
      return true;
    }
    return Object.freeze({ send, reset, diagnostics: () => ({ ...stats, pending: pending.size }) });
  }

  function createLocalAdapter(options = {}) {
    const enabled = options.enabled === true && ["memory", "ga4"].includes(options.mode);
    const flows = new Map(), records = [];
    const rules = Object.freeze({
      tryon: ["tryon_opened", "tryon_first_render_ready", "ui_technical_failure"],
      configurator: ["configurator_opened", "configuration_completed"]
    });
    const reasons = new Set(["assets_or_dialog", "frame_asset", "preset_image"]);
    let generation = 0, queued = 0, accepted = 0;
    function reset() { generation += 1; queued = 0; flows.clear(); records.length = 0; }
    function allowed() {
      if (!enabled) return false;
      try { if (options.consent() === "granted") return true; } catch {}
      reset();
      return false;
    }
    function enqueue(record, handle) {
      if (accepted >= 500) return false;
      const ticket = generation;
      queued += 1;
      accepted += 1;
      try {
        options.schedule(() => {
          if (ticket !== generation) return;
          queued -= 1;
          if (!allowed()) return;
          if (options.mode === "ga4") {
            try { options.transport.send(Object.freeze(record), () => ticket === generation && allowed()); } catch {}
          } else records.push(Object.freeze(record));
        });
        return true;
      } catch { reset(); return false; }
    }
    function create(flow, entry) {
      if (!allowed() || !Object.hasOwn(rules, flow) || !["product", "collection"].includes(entry) || flows.size >= 8) return null;
      const handle = Object.freeze({});
      flows.set(handle, { flow, entry, seen: new Set(), detailSeen: new Set(), lastOptions: new Map() });
      return handle;
    }
    function mark(handle, event, reason) {
      if (!allowed() || options.mode === "ga4" && !PUBLIC_EVENTS.has(event)) return false;
      const item = flows.get(handle);
      if (!item || !rules[item.flow].includes(event) || item.seen.has(event)) return false;
      const record = { event_name: event, entry_point: item.entry, schema_version: 1 };
      if (event === "ui_technical_failure") {
        if (!reasons.has(reason)) return false;
        record.component = "tryon";
        record.failure_code = reason;
      }
      item.seen.add(event);
      return enqueue(record, handle);
    }
    function detail(handle, event, fields) {
      if (!allowed()) return false;
      const item = flows.get(handle);
      if (!item || item.flow !== "configurator") return false;
      const safe = safeDetail(event, fields);
      if (!safe) return false;
      const key = event + ":" + safe.step + ":" + safe.visit_index;
      if (["configurator_step_viewed", "configurator_price_reviewed"].includes(event)) {
        if (item.detailSeen.has(key)) return false;
        item.detailSeen.add(key);
      }
      if (event === "configurator_option_selected") {
        const optionKey = key + ":" + safe.option_group;
        if (item.lastOptions.get(optionKey) === safe.option_value) return false;
        item.lastOptions.set(optionKey, safe.option_value);
      }
      return enqueue({ event_name: event, entry_point: item.entry, schema_version: 2, ...safe }, handle);
    }
    function begin(flow, entry) {
      const handle = create(flow, entry);
      if (handle) mark(handle, flow === "tryon" ? "tryon_opened" : "configurator_opened");
      return handle;
    }
    function end(handle) { flows.delete(handle); }
    function failed(entry, reason) {
      if (options.mode === "ga4") return;
      const handle = create("tryon", entry);
      if (handle) { mark(handle, "ui_technical_failure", reason); end(handle); }
    }
    function snapshot() { return allowed() ? records.map(record => ({ ...record })) : []; }
    return Object.freeze({ begin, mark, detail, end, failed, reset, snapshot });
  }

  let pageActive = true;
  function privacyDecision() {
    if (!pageActive) return "denied";
    try {
      const privacy = window.Shopify?.customerPrivacy;
      if (!privacy || typeof privacy.analyticsProcessingAllowed !== "function") return "unknown";
      return privacy.analyticsProcessingAllowed() === true ? "granted" : "denied";
    } catch { return "unknown"; }
  }
  const runtimeEnabled = ENABLED && document.currentScript?.dataset.elookusBehavior === "enabled";
  const transport = createGA4Transport({
    consent: privacyDecision,
    origin: () => window.location.origin,
    now: () => Date.now(),
    schedule: (callback, delay) => window.setTimeout(callback, delay),
    cancel: timer => window.clearTimeout(timer),
    tag: () => typeof window.gtag === "function" ? window.gtag.bind(window) : null
  });
  const runtime = createLocalAdapter({ enabled: runtimeEnabled, mode: "ga4", consent: privacyDecision, schedule: callback => window.setTimeout(callback, 0), transport });
  function resetAll() { runtime.reset(); transport.reset(); }
  if (runtimeEnabled) {
    document.addEventListener("visitorConsentCollected", () => { if (privacyDecision() !== "granted") resetAll(); });
    window.addEventListener("pagehide", () => { pageActive = false; resetAll(); });
    window.addEventListener("pageshow", () => { pageActive = true; });
    try {
      if (!window.Shopify?.customerPrivacy && typeof window.Shopify?.loadFeatures === "function") {
        window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], () => {});
      }
    } catch {}
  }
  window.ElookusBehaviorV1 = Object.freeze({ ...runtime, reset: resetAll, createLocalAdapter, createGA4Transport, enabled: runtimeEnabled, mode: "ga4", destination: GA4_ID, diagnostics: transport.diagnostics });
})();
